import type { JSX, ChangeEvent } from 'react';
import { useId } from 'react';
import { Input } from './ui';
import { useAmountPercentageToggle, type InputMode } from '../hooks/useAmountPercentageToggle';
import type { PensionType } from '../types';

export interface PensionInputProps {
  /**
   * Current percentage value (e.g., 5 for 5%)
   */
  percentageValue: number;

  /**
   * Current amount value (e.g., 5000 for £5000)
   */
  amountValue: number;

  /**
   * Which mode is currently active ('percentage' or 'amount')
   */
  activeMode: InputMode;

  /**
   * Change handler - receives value and type
   * Parent should update the appropriate value based on type
   */
  onChange: (value: number, type: InputMode) => void;

  /**
   * Optional label (defaults to "Pension Contributions")
   */
  label?: string;

  /**
   * Current pension type
   */
  pensionType?: PensionType;

  /**
   * Handler for pension type changes
   */
  onPensionTypeChange?: (type: PensionType) => void;
}

const PENSION_TYPE_LABELS: Record<PensionType, string> = {
  salary_sacrifice: 'Salary Sacrifice',
  relief_at_source: 'Relief at Source',
  net_pay: 'Net Pay',
};

const PENSION_TYPE_DESCRIPTIONS: Record<PensionType, string> = {
  salary_sacrifice: 'Deducted before tax and National Insurance (most common)',
  relief_at_source: 'You pay after tax, provider claims 25% from HMRC',
  net_pay: 'Deducted before income tax, after National Insurance',
};

/**
 * PensionInput component - handles pension contributions with type selector and independent percentage/amount toggle
 *
 * Features:
 * - Switch between amount (£) and percentage (%) input
 * - NO automatic conversion - values are independent
 * - Only the active value is sent to backend
 * - Select pension type (Salary Sacrifice, Relief at Source, Net Pay)
 * - Helper text explaining each pension type
 *
 * @example
 * ```tsx
 * <PensionInput
 *   percentageValue={5}
 *   amountValue={5000}
 *   activeMode="percentage"
 *   onChange={(value, type) => {
 *     if (type === 'percentage') {
 *       setPensionPercentage(value);
 *     } else {
 *       setPensionAmount(value);
 *     }
 *     setPensionType(type);
 *   }}
 *   pensionType="salary_sacrifice"
 *   onPensionTypeChange={(type) => setPensionType(type)}
 * />
 * ```
 */
export function PensionInput({
  percentageValue,
  amountValue,
  activeMode,
  onChange,
  label = 'Pension Contributions',
  pensionType = 'salary_sacrifice',
  onPensionTypeChange,
}: PensionInputProps): JSX.Element {
  const pensionTypeId = useId();
  const amountInputId = useId();

  const { inputMode, displayValue, handleInputChange, handleModeToggle } = useAmountPercentageToggle({
    percentageValue,
    amountValue,
    activeMode,
    onChange,
  });

  const handleTypeChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const newType = e.target.value as PensionType;
    onPensionTypeChange?.(newType);
  };

  return (
    <div>
      {/* Pension Type Selector */}
      {onPensionTypeChange && (
        <div className="mb-4">
          <label htmlFor={pensionTypeId} className="block text-sm font-medium text-gray-700 mb-1">
            Pension Type
          </label>
          <select
            id={pensionTypeId}
            value={pensionType}
            onChange={handleTypeChange}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors"
          >
            {(Object.entries(PENSION_TYPE_LABELS) as [PensionType, string][]).map(([type, typeLabel]) => (
              <option key={type} value={type}>
                {typeLabel}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">
            {PENSION_TYPE_DESCRIPTIONS[pensionType]}
          </p>
        </div>
      )}

      {/* Amount Input with Toggle */}
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={amountInputId} className="text-sm font-medium text-gray-700">
          {label}
        </label>
        <button
          type="button"
          onClick={handleModeToggle}
          className="text-sm text-primary hover:opacity-80 transition-opacity font-medium"
          aria-label={`Switch to ${inputMode === 'amount' ? 'percentage' : 'amount'} mode`}
        >
          Switch to {inputMode === 'amount' ? '%' : '£'}
        </button>
      </div>
      <Input
        id={amountInputId}
        type="number"
        value={displayValue}
        onChange={handleInputChange}
        prefix={inputMode === 'amount' ? '£' : undefined}
        suffix={inputMode === 'percentage' ? '%' : undefined}
        helperText="Your pension contributions"
        fullWidth
        selectAllOnFocus
      />
    </div>
  );
}
